import { __assign } from "tslib";
import { namespacedClassnames } from '../../../common/namespace-classes';
import * as React from 'react';
import { ReactSelect3, ReactSelect3Components } from '../../../common/react-select';
import { LibColor } from '../../../new-libby/core/colors/colors-map';
import { Text } from '../../../new-libby/core';
import { menuFooterStyle, selectV3Styles } from '../select-v3-styles';
import { Tooltip } from '../..';
import { getChevronIcon } from '../chevron-icon';
var SELECT_MENU_HEIGHT = 172;
var defaultV3Props = {
    disabled: false,
    searchable: true,
    openMenuOnFocus: true,
    clearable: false,
    autosize: false
};
export var Select = function (props) {
    var _a = React.useState(''), query = _a[0], setQuery = _a[1];
    var refSelectV3 = props.innerRef || React.useRef(null);
    var formatOptionLabel = function (data, labelMeta) {
        var _a;
        if (labelMeta.context === 'menu') {
            var isSelected = !!((_a = labelMeta === null || labelMeta === void 0 ? void 0 : labelMeta.selectValue) === null || _a === void 0 ? void 0 : _a.find(function (value) { return value.value === data.value; }));
            var optionState = { isSelected: isSelected };
            return props.optionRenderer ? props.optionRenderer(data, optionState) : data.label;
        }
        else {
            return props.valueRenderer ? props.valueRenderer(data) : data.label;
        }
    };
    // avoids blowup when onInputChange passed as async - https://app.asana.com/0/1198918337991486/1199694602056889/f
    var onInputChange = function (term) {
        setQuery(term);
        if (props.onInputChange) {
            props.onInputChange(term);
        }
    };
    // avoids the Menu from opening again because clicking on the Footer is still considered a focus event
    var onClickFooter = function (e) {
        var _a, _b;
        var footerCompClass = (_a = e.target) === null || _a === void 0 ? void 0 : _a.classList.value;
        if (refSelectV3.current) {
            // checks if footer comp is a link
            if (!(footerCompClass === null || footerCompClass === void 0 ? void 0 : footerCompClass.includes('aul link'))) {
                e.preventDefault();
                e.stopPropagation();
            }
            (_b = refSelectV3.current) === null || _b === void 0 ? void 0 : _b.blur();
        }
    };
    var onMouseDownHandler = function (option) { return function (e) {
        var _a;
        if (!option.isDisabled) {
            props.onChange(option);
        }
        e.preventDefault();
        e.stopPropagation();
        if (refSelectV3.current) {
            if (!!props.blurInputOnSelect || props.blurInputOnSelect === undefined) {
                (_a = refSelectV3.current) === null || _a === void 0 ? void 0 : _a.blur();
            }
        }
    }; };
    var renderMenuFooter = function (menuProps) {
        if (props.menuFooter) {
            var isMenuPlacementTop = menuProps.menuPlacement === 'top' ? true : false;
            var menuFooterComp = (React.createElement("div", { className: 'select-menu-footer', style: menuFooterStyle(isMenuPlacementTop), onClick: onClickFooter }, props.menuFooter));
            return isMenuPlacementTop ? (React.createElement(React.Fragment, null,
                menuFooterComp,
                menuProps.children)) : (React.createElement(React.Fragment, null,
                menuProps.children,
                menuFooterComp));
        }
        else {
            return menuProps.children;
        }
    };
    var handleOptionAffix = function (optionProps) {
        var maybeSuffix = optionProps.data.suffix ? (React.createElement(Text, { className: 'select-v3-option-suffix', color: 't40', type: 'tl' },
            " ",
            optionProps.data.suffix)) : null;
        return (React.createElement(React.Fragment, null,
            optionProps.children,
            maybeSuffix));
    };
    var renderV3 = function () {
        var Menu = function (menuProps) {
            return (React.createElement(ReactSelect3Components.Menu, __assign({}, menuProps), renderMenuFooter(menuProps)));
        };
        var DropdownIndicator = function (dropdownProps) {
            return (React.createElement(ReactSelect3Components.DropdownIndicator, __assign({}, dropdownProps), getChevronIcon(!!props.disabled)));
        };
        var IndicatorSeparator = function (_) { return null; };
        var NoOptionsMessage = function () {
            var noOptionStyle = { fontSize: '14px', fontWeight: 300, padding: '6px 12px', color: "" + LibColor.grey100 };
            var noOptionsElem = typeof props.noOptionsMessage === 'string' ?
                React.createElement("div", { className: 'empty-state-v3', style: noOptionStyle }, props.noOptionsMessage) :
                props.noOptionsMessage;
            var noResultsTextElem;
            switch (typeof props.noResultsText) {
                case 'string':
                    noResultsTextElem = React.createElement("div", { className: 'no-results-v3', style: noOptionStyle }, props.noResultsText);
                    break;
                case 'function':
                    noResultsTextElem = props.noResultsText(query);
                    break;
                default:
                    noResultsTextElem = props.noResultsText;
                    break;
            }
            return (query ? noResultsTextElem : noOptionsElem) ||
                (noOptionsElem || React.createElement("div", { style: noOptionStyle }, "No results found"));
        };
        var Option = function (optionProps) {
            var data = handleOptionAffix(optionProps);
            var additionalProps = (!!props.blurInputOnSelect || props.blurInputOnSelect === undefined) ?
                { innerProps: __assign(__assign({}, optionProps.innerProps), { onMouseDown: onMouseDownHandler(optionProps.data) }) } : {};
            return (React.createElement(ReactSelect3Components.Option, __assign({}, optionProps, { className: optionProps.data.className }, additionalProps), data));
        };
        var SingleValue = function (optionProps) {
            var data = handleOptionAffix(optionProps);
            return (React.createElement(ReactSelect3Components.SingleValue, __assign({}, optionProps, { className: optionProps.data.className }), data));
        };
        var classNamesV3 = namespacedClassnames('new-select', props.className, { small: props.size === 'small' });
        var maybeMenuPortal = props.bringMenuToFront ? {
            menuPortalTarget: document.body,
            // tslint:disable-next-line: max-line-length
            menuShouldBlockScroll: props.menuShouldBlockScroll !== undefined ? props.menuShouldBlockScroll : true // Issue with menu repositioning on scroll so we need this - https://github.com/JedWatson/react-select/issues/4088
        } : {};
        return (React.createElement(ReactSelect3, __assign({}, defaultV3Props, maybeMenuPortal, { ref: refSelectV3, value: props.value || null, options: props.options, onChange: props.onChange, placeholder: props.placeholder, isDisabled: !!props.disabled, classNamePrefix: 'select-v3', className: classNamesV3, components: __assign({ DropdownIndicator: DropdownIndicator, IndicatorSeparator: IndicatorSeparator, NoOptionsMessage: NoOptionsMessage, Option: Option, SingleValue: SingleValue, Menu: Menu }, props.components), styles: selectV3Styles(props.size, props.menuPlacement, false, props.styles), blurInputOnSelect: props.blurInputOnSelect, isSearchable: props.searchable, onBlur: props.onBlur, onFocus: props.onFocus, onMenuOpen: props.onOpen, onMenuClose: props.onClose, onInputChange: onInputChange, autoFocus: props.autoFocus, formatOptionLabel: formatOptionLabel, menuPlacement: props.menuPlacement, menuShouldScrollIntoView: true, maxMenuHeight: SELECT_MENU_HEIGHT, searchPromptText: props.searchPromptText, filterOption: props.disableOptionFiltering ? function () { return true; } : undefined, menuShouldBlockScroll: false, onKeyDown: props.onKeyDown })));
    };
    var shouldRenderWithTooltip = function (disabled) {
        return !!disabled && typeof disabled !== 'boolean';
    };
    var selectComp = renderV3();
    return shouldRenderWithTooltip(props.disabled) ? (React.createElement(Tooltip, { body: props.disabled, relativeToBody: true, className: 'disabled-select-tooltip', popperTargetClassName: props.popperTargetClassName }, selectComp)) : selectComp;
};
//# sourceMappingURL=index.js.map